import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { X, CheckCircle, CreditCard, Calendar, Clock, Sparkle, Video, HouseSimple, UsersThree } from '@phosphor-icons/react';
import { formatPrice, MODES } from '@/data/wellness';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MODE_ICON = { online: Video, in_person: UsersThree, home_visit: HouseSimple };

const BookingModal = ({ open, onClose, pro, user }) => {
  const [step, setStep] = useState(1); // 1: details, 2: payment, 3: success
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');

  const [form, setForm] = useState({
    mode: pro?.modes?.[0] || 'online',
    date: '',
    time: '10:00',
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: '',
    notes: '',
  });

  // Fetch slots when modal opens
  React.useEffect(() => {
    if (open && pro?.slug) {
      axios.get(`${BACKEND_URL}/api/wellness/professionals/${pro.slug}/slots`)
        .then((res) => setSlots(res.data.slots || []))
        .catch(() => setSlots([]));
    }
  }, [open, pro?.slug]);

  const minDate = useMemo(() => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return d.toISOString().split('T')[0];
  }, []);

  const timezone_name = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const grossAmount = pro?.price_from || 0;
  const currency = pro?.currency || 'USD';

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const reset = () => {
    setStep(1);
    setConfirmation(null);
    setForm({
      mode: pro?.modes?.[0] || 'online',
      date: '', time: '10:00',
      customer_name: user?.name || '',
      customer_email: user?.email || '',
      customer_phone: '',
      notes: '',
    });
  };

  const handleClose = () => { reset(); onClose(); };

  const goToPayment = (e) => {
    e.preventDefault();
    if (!form.date || !form.time || !form.customer_name || !form.customer_email) {
      toast.error('Please fill all required fields');
      return;
    }
    setStep(2);
  };

  const handleMockPay = async () => {
    setSubmitting(true);
    try {
      // Build scheduled_at ISO in user local tz then convert to UTC
      const local = new Date(`${form.date}T${form.time}:00`);
      const scheduled_at = local.toISOString();
      const payload = {
        professional_id: pro.professional_id,
        service_name: `${pro.professional_type} — 1:1 Consultation`,
        mode: form.mode,
        scheduled_at,
        timezone_name,
        gross_amount: grossAmount,
        currency,
        customer_email: form.customer_email,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        notes: form.notes,
      };
      const res = await axios.post(`${BACKEND_URL}/api/wellness/bookings`, payload);
      setConfirmation(res.data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-4 py-3 outline-none transition-all text-[15px] text-[#1C1917] placeholder:text-stone-400";
  const labelCls = "block text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5";

  if (!pro) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={handleClose}
          data-testid="booking-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 26, stiffness: 260 }}
            className="relative w-full max-w-lg bg-white rounded-[2rem] shadow-2xl overflow-hidden my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#1A4331] to-[#2A5A44] px-6 pt-6 pb-14 overflow-hidden">
              <div className="pointer-events-none absolute -top-16 -right-16 w-52 h-52 bg-[#34D399]/20 rounded-full blur-3xl" />
              <button onClick={handleClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-all" data-testid="booking-close-btn">
                <X size={18} weight="bold" />
              </button>
              <div className="relative">
                <p className="text-xs uppercase tracking-[0.14em] text-[#34D399] font-bold mb-1">
                  {step === 1 ? 'Step 1 of 2 · Details' : step === 2 ? 'Step 2 of 2 · Payment' : 'Confirmed'}
                </p>
                <h2 className="text-xl md:text-2xl text-white font-heading font-medium tracking-tight">
                  {step === 3 ? 'You\'re booked!' : `Book with ${pro.full_name}`}
                </h2>
              </div>
            </div>

            <div className="relative -mt-8 px-4 pb-6">
              <div className="bg-white rounded-3xl border border-stone-100 shadow-xl shadow-stone-200/40 p-6">
                {/* STEP 1: DETAILS */}
                {step === 1 && (
                  <form onSubmit={goToPayment} className="space-y-4">
                    <div>
                      <label className={labelCls}>Mode</label>
                      <div className="flex gap-2 flex-wrap">
                        {(pro.modes || []).map((m) => {
                          const M = MODE_ICON[m];
                          const label = MODES.find((x) => x.id === m)?.label;
                          return (
                            <button
                              key={m}
                              type="button"
                              onClick={() => setForm({ ...form, mode: m })}
                              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${form.mode === m ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1A4331]'}`}
                              data-testid={`booking-mode-${m}`}
                            >
                              {M && <M size={14} weight="duotone" />} {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Date *</label>
                        <input type="date" value={form.date} onChange={setField('date')} min={minDate} className={inputCls} required data-testid="booking-date" />
                      </div>
                      <div>
                        <label className={labelCls}>Time *</label>
                        <input type="time" value={form.time} onChange={setField('time')} className={inputCls} required data-testid="booking-time" />
                      </div>
                    </div>

                    {slots.length > 0 && (
                      <div>
                        <label className={labelCls}>Or pick an available slot</label>
                        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto bg-stone-50 rounded-2xl p-3" data-testid="slot-picker">
                          {slots.slice(0, 20).map((s) => {
                            const d = new Date(s.start);
                            const label = `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
                            const isSelected = selectedSlot === s.start;
                            return (
                              <button
                                key={s.start}
                                type="button"
                                onClick={() => {
                                  setSelectedSlot(s.start);
                                  const iso = new Date(s.start);
                                  setForm({ ...form, date: iso.toISOString().split('T')[0], time: iso.toTimeString().slice(0, 5) });
                                }}
                                className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-all ${isSelected ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1A4331]'}`}
                                data-testid={`slot-${s.start}`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-stone-500 -mt-1">Your local timezone: <span className="font-semibold">{timezone_name}</span></p>

                    <div className="pt-2 border-t border-stone-100">
                      <p className={labelCls}>Your Info</p>
                      <input type="text" value={form.customer_name} onChange={setField('customer_name')} placeholder="Full Name *" className={inputCls} required data-testid="booking-name" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <input type="email" value={form.customer_email} onChange={setField('customer_email')} placeholder="Email *" className={inputCls} required data-testid="booking-email" />
                        <input type="tel" value={form.customer_phone} onChange={setField('customer_phone')} placeholder="Phone" className={inputCls} data-testid="booking-phone" />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>Notes <span className="text-stone-400 font-normal normal-case tracking-normal">(Optional)</span></label>
                      <textarea rows={2} value={form.notes} onChange={setField('notes')} placeholder="What would you like help with?" className={`${inputCls} resize-none`} />
                    </div>

                    <button type="submit" className="w-full bg-[#1A4331] text-white py-3.5 rounded-full font-semibold hover:bg-[#2A5A44] transition-all shadow-md flex items-center justify-center gap-2" data-testid="booking-continue-btn">
                      Continue to Payment
                    </button>
                  </form>
                )}

                {/* STEP 2: PAYMENT */}
                {step === 2 && (
                  <div className="space-y-4" data-testid="booking-payment-step">
                    <div className="bg-stone-50 rounded-2xl p-5 space-y-2">
                      <div className="flex justify-between text-sm"><span className="text-stone-500">Professional</span><span className="font-semibold text-[#1C1917]">{pro.full_name}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-stone-500">Service</span><span className="font-semibold text-[#1C1917]">1:1 Consultation</span></div>
                      <div className="flex justify-between text-sm"><span className="text-stone-500">When</span><span className="font-semibold text-[#1C1917]">{form.date} · {form.time}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-stone-500">Mode</span><span className="font-semibold text-[#1C1917] capitalize">{form.mode.replace('_', ' ')}</span></div>
                      <div className="border-t border-stone-200 pt-2 mt-2">
                        <div className="flex justify-between"><span className="text-stone-600 font-semibold">Total</span><span className="text-xl font-semibold text-[#1A4331] font-heading" data-testid="booking-total">{formatPrice(grossAmount, currency)}</span></div>
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-3 text-xs flex gap-2">
                      <Sparkle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
                      <div><strong>Demo mode:</strong> This is a MOCK PAYMENT. No card or money is actually charged. Real payment gateway will be integrated later.</div>
                    </div>

                    <div className="flex gap-2">
                      <button onClick={() => setStep(1)} className="flex-1 bg-white border border-stone-200 text-stone-700 py-3.5 rounded-full font-semibold hover:border-[#1A4331] transition-all" data-testid="booking-back-btn">
                        Back
                      </button>
                      <button
                        onClick={handleMockPay}
                        disabled={submitting}
                        className="flex-1 bg-[#1A4331] text-white py-3.5 rounded-full font-semibold hover:bg-[#2A5A44] transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="booking-mock-pay-btn"
                      >
                        {submitting ? (<><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Processing...</>) : (<><CreditCard size={16} weight="duotone" /> Pay {formatPrice(grossAmount, currency)}</>)}
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 3: SUCCESS */}
                {step === 3 && confirmation && (
                  <div className="text-center py-2" data-testid="booking-success">
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 14 }}
                      className="w-20 h-20 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center mx-auto mb-5 shadow-xl shadow-[#10B981]/30"
                    >
                      <CheckCircle size={40} weight="fill" className="text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-medium font-heading text-[#1C1917] mb-2 tracking-tight">Booking Confirmed!</h3>
                    <p className="text-stone-600 text-sm mb-5 max-w-sm mx-auto leading-relaxed">
                      A confirmation email is on its way. See you on <strong>{form.date} at {form.time}</strong>.
                    </p>
                    <div className="bg-stone-50 rounded-2xl px-5 py-4 mb-5 inline-block">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-1">Booking ID</p>
                      <p className="text-lg font-bold text-[#1A4331] font-heading tracking-tight" data-testid="booking-id">{confirmation.booking_id}</p>
                    </div>
                    {confirmation.meeting_url && (
                      <div className="mb-4">
                        <a href={confirmation.meeting_url} target="_blank" rel="noreferrer" className="text-sm text-[#1A4331] font-semibold hover:underline">
                          <Video size={14} weight="duotone" className="inline mr-1" /> Join Meeting Link (demo)
                        </a>
                      </div>
                    )}
                    <button
                      onClick={() => { handleClose(); window.location.href = `/wellness/my-wellness?email=${encodeURIComponent(form.customer_email)}`; }}
                      className="bg-[#1A4331] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#2A5A44] transition-all shadow-md"
                      data-testid="booking-view-dashboard-btn"
                    >
                      Go to My Wellness
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
